/**
 * eSocial Event-Specific Metadata Types
 * These interfaces mirror the metadata structures that XmlBuilders consumes.
 */

/** S-2210 CAT metadata - matches XmlBuilders.buildS2210Xml input */
export interface S2210Metadata {
  ideEvento: { indRetif: number; tpAmb: number; procEmi: number; verProc: string };
  ideEmpregador: { tpInsc: number; nrInsc: string };
  ideVinculo: { cpfTrab: string; matricula?: string };
  cat: {
    dtAcid: string;
    tpAcid: number;
    hrAcid?: string;
    hrsTrabAntesAcid?: string;
    tpCat: number;
    indCatObito?: string;
    dtObito?: string;
    indComunPolicia?: string;
    codSitGeradora?: string;
    iniciatCAT: number;
    obsCAT?: string;
    localAcidente?: {
      tpLocal: number;
      dscLocal?: string;
      codAmb?: string;
      tpLograd?: string;
      dscLograd?: string;
      nrLograd?: string;
      bairro?: string;
      cep?: string;
      codMunic?: number;
      uf?: string;
      pais?: string;
    };
    parteAtingida?: {
      codParteAting: string;
      lateralidade: number;
    };
    agenteCausador?: {
      codAgntCausador: string;
    };
    atestado?: {
      dtAtend: string;
      hrAtend?: string;
      indInternacao: string;
      durTrat: number;
      indAfast: string;
      dscLesao?: string;
      dscCompLesao?: string;
      diagProvavel?: string;
      codCID: string;
      observacao?: string;
      emitente?: {
        nmEmit: string;
        ideOC: number;
        nrOC: string;
        ufOC?: string;
      };
    };
  };
}

/** S-2220 ASO metadata - matches XmlBuilders.buildS2220Xml input */
export interface S2220Metadata {
  ideEvento: { indRetif: number; tpAmb: number; procEmi: number; verProc: string };
  ideEmpregador: { tpInsc: number; nrInsc: string };
  ideVinculo: { cpfTrab: string; matricula?: string };
  exMedOcup: {
    tpExameOcup: number;
    aso: {
      dtAso: string;
      resAso: number;
      exames?: Array<{
        dtExm: string;
        procRealizado: string;
        obsProc?: string;
        ordExame: number;
        indResult?: number;
      }>;
    };
    medico: {
      nmMed: string;
      nrCRM: string;
      ufCRM: string;
    };
    respMonit?: {
      nmResp: string;
      nrCRM: string;
      ufCRM: string;
    };
  };
}

/** S-2240 Environmental Conditions metadata - matches XmlBuilders.buildS2240Xml input */
export interface S2240Metadata {
  ideEvento: { indRetif: number; tpAmb: number; procEmi: number; verProc: string };
  ideEmpregador: { tpInsc: number; nrInsc: string };
  ideVinculo: { cpfTrab: string; matricula?: string };
  infoExpRisco: {
    dtIniCondicao: string;
    infoAmb?: {
      codAmb: string;
      dscSetor?: string;
      tpInsc?: number;
      nrInsc?: string;
    };
    infoAtiv?: {
      dscAtivDes: string;
    };
    agNoc: Array<{
      codAgNoc: string;
      dscAgNoc?: string;
      tpAval?: number;
      intConc?: number;
      limTol?: number;
      unMed?: number;
      tecMedicao?: string;
      epcEpi?: {
        utilizEPC: number;
        utilizEPI: number;
        epc?: Array<{ dscEpc: string }>;
        epi?: Array<{ caEPI: string; dscEPI?: string; eficEpi: string }>;
      };
    }>;
    respReg: Array<{
      cpfResp: string;
      nmResp: string;
      ideOC: number;
      nrOC: string;
      ufOC?: string;
    }>;
  };
}

/** Union type for event metadata */
export type ESocialEventMetadata = S2210Metadata | S2220Metadata | S2240Metadata;

/** Helper: get default metadata for an event type */
export function getDefaultMetadata(eventType: string): Partial<ESocialEventMetadata> | null {
  const baseIdeEvento = { indRetif: 1, tpAmb: 2, procEmi: 1, verProc: '1.0' };

  switch (eventType) {
    case 'S_2210':
      return {
        ideEvento: baseIdeEvento,
        ideEmpregador: { tpInsc: 1, nrInsc: '' },
        ideVinculo: { cpfTrab: '' },
        cat: { dtAcid: '', tpAcid: 1, tpCat: 1, iniciatCAT: 1 },
      } as Partial<S2210Metadata>;
    case 'S_2220':
      return {
        ideEvento: baseIdeEvento,
        ideEmpregador: { tpInsc: 1, nrInsc: '' },
        ideVinculo: { cpfTrab: '' },
        exMedOcup: { tpExameOcup: 0, aso: { dtAso: '', resAso: 1 }, medico: { nmMed: '', nrCRM: '', ufCRM: '' } },
      } as Partial<S2220Metadata>;
    case 'S_2240':
      return {
        ideEvento: baseIdeEvento,
        ideEmpregador: { tpInsc: 1, nrInsc: '' },
        ideVinculo: { cpfTrab: '' },
        infoExpRisco: { dtIniCondicao: '', agNoc: [], respReg: [] },
      } as Partial<S2240Metadata>;
    default:
      return null;
  }
}
